/**
 * GHL 7.6 — acciones en lote sobre leads.
 *
 * El backend las expone como POST /crm/leads/bulk/{assign|update-stage|
 * update-priority|soft-delete} (lead-bulk.controller.ts). El front llamaba a
 * PATCH /crm/leads/bulk-assign|bulk-update y DELETE /crm/leads/bulk-delete,
 * rutas que no existen: ninguna acción en lote ni el arrastre del Kanban se
 * guardaban.
 *
 * La etapa que se guarda es `lead.stage` (LeadStage del backend, 7 valores).
 * Las etapas del embudo por rol (/crm/funnel/stages, 23 valores) son otro
 * vocabulario: sólo coinciden `negotiation` y `closed_won`, así que el Kanban
 * no puede usarlas como columnas destino.
 */

export const ETAPAS_LEAD: { valor: string; etiqueta: string }[] = [
  { valor: 'new', etiqueta: 'Nuevo' },
  { valor: 'contacted', etiqueta: 'Contactado' },
  { valor: 'qualified', etiqueta: 'Calificado' },
  { valor: 'proposal', etiqueta: 'Propuesta' },
  { valor: 'negotiation', etiqueta: 'Negociación' },
  { valor: 'closed_won', etiqueta: 'Ganado' },
  { valor: 'closed_lost', etiqueta: 'Perdido' },
];

export const PRIORIDADES_LEAD: { valor: string; etiqueta: string }[] = [
  { valor: 'low', etiqueta: 'Baja' },
  { valor: 'medium', etiqueta: 'Media' },
  { valor: 'high', etiqueta: 'Alta' },
  { valor: 'urgent', etiqueta: 'Urgente' },
];

export const etiquetaEtapa = (v: string): string =>
  ETAPAS_LEAD.find((e) => e.valor === v)?.etiqueta ?? (v === 'unassigned' ? 'Sin etapa' : v);

/** Columnas del Kanban: las 7 etapas en orden, más cualquier etapa rara que traigan los leads. */
export const columnasKanban = (presentes: string[]): string[] => {
  const base = ETAPAS_LEAD.map((e) => e.valor);
  return [...base, ...presentes.filter((p) => !base.includes(p))];
};

/**
 * Lista de agentes para asignar. El DTO de `GET /agency/agents` rechaza `limit`
 * mayor que 100 con un 400 («limit must not be greater than 100»), y el catch
 * del llamador lo deja en una lista vacía sin avisar. Por eso la ruta vive en un
 * solo lugar y un test de clase vigila que nadie pida más.
 */
export const LIMITE_AGENTES = 100;
export const RUTA_AGENTES_ASIGNABLES = `/agency/agents?limit=${LIMITE_AGENTES}`;

/**
 * GHL 7.6 — agentes a los que se puede asignar un lead. `lead.assignedToId` es
 * un USUARIO (lead.entity.ts: ManyToOne UserEntity), no el id del agente: se
 * usa `agent.userId`, y un agente sin cuenta vinculada no se puede elegir.
 */
export function agentesAsignables(respuesta: unknown): { id: string; fullName: string }[] {
  const r: any = respuesta;
  const lista: any[] = Array.isArray(r) ? r : r?.agents ?? r?.data?.agents ?? r?.data ?? [];
  return (Array.isArray(lista) ? lista : [])
    .filter((a) => typeof a?.userId === 'string' && a.userId)
    .map((a) => ({ id: a.userId, fullName: `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.email || 'Agente' }));
}

/** Ruta y cuerpo del cambio en lote. Un cambio sin endpoint tira error, no se manda a ciegas. */
export function pedidoCambioMasivo(
  leadIds: string[],
  cambios: { stage?: string; priority?: string },
): { ruta: string; cuerpo: Record<string, unknown> } {
  if (cambios.stage) {
    if (!ETAPAS_LEAD.some((e) => e.valor === cambios.stage)) {
      throw new Error(`La etapa «${cambios.stage}» no se puede guardar`);
    }
    return { ruta: '/crm/leads/bulk/update-stage', cuerpo: { leadIds, stage: cambios.stage } };
  }
  if (cambios.priority) {
    return { ruta: '/crm/leads/bulk/update-priority', cuerpo: { leadIds, priority: cambios.priority } };
  }
  throw new Error('Ese cambio en lote no está disponible');
}
